<script lang="ts">
	import { page } from '$app/state'
	import Modal from '#components/modal.svelte'
	import Field from '#components/field.svelte'
	import Checkbox from '#components/checkbox.svelte'
	import Button from '#components/button.svelte'
	import { ui } from '#library/stores.ts'
	import { enhance } from '$app/forms'
	import { slide } from 'svelte/transition'
	import { attrs } from '#library/attrs.ts'

	let { pattern } = $props()
	let {
		foot_title,
		foot_menu,
		foot_shortcuts,
		address,
		contact_tel,
		contact_email,
		charity,
		subscribe,
		socials,
		partners
	} = $derived(page.data.layout)

	let form_state: 'idle' | 'submitting' | 'success' | 'error' = $state('idle')

	type SubmitFunction = Parameters<typeof enhance>[1]

	const submit: SubmitFunction = () => {
		form_state = 'submitting'

		return async ({ result }) => {
			switch (result.type) {
				case 'success':
					form_state = 'success'
					$ui.subscribe = false
					$ui.email = ''

					break

				case 'error':
				case 'failure':
					form_state = 'error'
					break
			}
		}
	}
</script>

<footer class="foot" style:--pattern={pattern} class:search={$ui.search} {...attrs(page.data.layout)}>
	<div class="content">
		<div class="contain">
			<div>
				<a class="logo" href="/" aria-label="Home">
					<svg class="logo-word"><use xlink:href="/images/vector.svg#logo"></use></svg>
				</a>
				<div class="info">
					<nav class="nav">
						<div class="subtitle">Quick Links</div>
						{#if foot_menu}
							{#each foot_menu as item, index (index)}
								<a class="link" href={item.link?.url}>{item.link?.text}</a>
							{/each}
						{/if}
						{#if foot_shortcuts}
							{#each foot_shortcuts as item, index (index)}
								<a class="link xlink" href={item.link?.url}>{item.link?.text}</a>
							{/each}
						{/if}
					</nav>
					<div>
						<div class="copy">
							<div class="subtitle">Contact us</div>
							<address class="address">
								{address}
							</address>
						</div>
						<div class="copy">
							<div class="subtitle">Reception</div>
							<div class="links">
								{#if contact_tel}
									<a href="tel:{contact_tel.replace(/\s/g, '').replace(/^0/, '+44')}">
										T: <span>{contact_tel}</span>
									</a>
								{/if}
								<a href="mailto:{contact_email}">E: <span>{contact_email}</span></a>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div class="fill">
				<div class="title">{foot_title}</div>
				<div class="xlinks">
					{#if foot_shortcuts}
						{#each foot_shortcuts as item, index (index)}
							<a class="link" href={item.link?.url}>{item.link?.text}</a>
						{/each}
					{/if}
				</div>
			</div>
			<hr class="hr" />
			<div class="follow">
				<form class="form">
					<label class="label" for="email">{subscribe}</label>
					<div class="field">
						<input
							class="input"
							type="email"
							id="email"
							name="email"
							placeholder="Enter your email"
							required
							bind:value={$ui.email}
						/>
						<button
							class="submit"
							type="button"
							aria-label="Subscribe"
							onclick={() => ($ui.subscribe = true)}
						>
							<svg class="icon"><use xlink:href="/images/vector.svg#arrow-right"></use></svg>
						</button>
					</div>
				</form>
				<div class="socials">
					{#if socials}
						{#each socials as social, index (index)}
							{@const { platform, link } = social}
							<a href={link} target="_blank" aria-label={platform} {...attrs(social)}>
								<svg class="social"><use xlink:href="/images/vector.svg#{platform}"></use></svg>
							</a>
						{/each}
					{/if}
				</div>
			</div>
			<div class="metas">
				<div class="meta">{charity}</div>
				<div class="meta">© Bedales School {new Date().getFullYear()}</div>
				<a class="meta" href="https://www.mad-keen.co.uk/" target="_blank">Website by Studio Mad Keen</a>
			</div>
		</div>
	</div>
	<div class="end">
		<figure class="pattern" style:--pattern={pattern}></figure>
		<div class="logos">
			{#if Array.isArray(partners) && partners?.length}
				{#each partners as partner (partner)}
					<img src={partner.filename} alt={partner.alt} width={partner.width} height={partner.height} />
				{/each}
			{/if}
		</div>
	</div>
</footer>

<Modal
	title="Subscribe to our newsletter"
	small
	close={() => (($ui.subscribe = false), ($ui.email = ''))}
	open={$ui.subscribe}
>
	<div>
		Join our newsletter for updates on life and learning at Bedales. Just tell us which age group you're curious
		about.
	</div>
	<form method="post" action="/?/newsletter_subscribe" use:enhance={submit}>
		<fieldset class="checkboxes">
			<legend data-required>What year group are you interested in?</legend>
			<Checkbox
				name="school"
				options={[
					{ id: 'preprep', value: 'preprep', label: 'Bedales Pre Prep (3-8yrs)' },
					{ id: 'prep', value: 'prep', label: 'Bedales Prep (8-13yrs)' },
					{ id: 'senior', value: 'senior', label: 'Bedales Senior (13-18yrs)' }
				]}
			/>
		</fieldset>
		<fieldset>
			<legend data-required>Your email</legend>
			<Field name="email" type="email" id="email" bind:value={$ui.email} required />
		</fieldset>
		<Button type="submit" loading={form_state === 'submitting'} fill>Subscribe</Button>
		{#if ['error', 'failure'].includes(form_state)}
			<p class="error" transition:slide>There was an error subscribing to our newsletter. Please try again.</p>
		{/if}
	</form>
</Modal>

<Modal
	title="Thank you for subscribing!"
	dialog
	close={() => (form_state = 'idle')}
	open={form_state === 'success'}
>
	<div>
		We look forward to sharing what our students are exploring, creating and thinking about — and what makes us
		different.
	</div>
	<Button type="submit" fill outline onclick={() => (form_state = 'idle')}>Close</Button>
</Modal>

<style land="css">
	.foot {
		transition: opacity 500ms ease;

		&.search {
			opacity: 0;
		}
	}
	.content {
		background-color: var(--c-gimson);
		margin-block-end: 80vh;
		padding-block: 4rem 0;
		z-index: 1;

		@media (--from-mobile) {
			padding-block: 6.25rem 0;
		}
	}

	.contain {
		display: grid;
		grid-template-columns: minmax(0, 1fr);

		@media (--from-mobile) {
			grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
		}

		@media (--from-laptop) {
			grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
		}
	}

	.fill {
		display: grid;

		@media (--until-mobile) {
			grid-column: 1 / -1;
		}
	}

	.logo-word {
		max-width: 9.75rem;
		max-height: 3rem;

		@media (--from-mobile) {
			max-width: 15.25rem;
			max-height: 3.625rem;
		}
	}

	.title {
		color: var(--c-crocus);
		font: 500 var(--f-h3);
		letter-spacing: var(--ls-h3);
		margin-block-start: 3rem;
		margin-inline-start: auto;
		max-width: 10ch;
		padding-inline-start: 1rem;

		@media (--from-mobile) {
			margin-block-start: 0;
			max-width: 12ch;
			padding-inline-start: 0;
			text-align: end;
		}
	}

	.xlinks {
		display: none;

		@media (--from-mobile) {
			display: grid;
			gap: 0.75rem;
			margin-block-start: auto;
			place-items: end;
		}
	}

	.xlink {
		@media (--from-mobile) {
			display: none;
		}
	}

	.info {
		align-items: start;
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		padding-block-start: 2.25rem;

		@media (--from-mobile) {
			gap: 1rem;
			padding-block-start: 2.5rem;
		}
	}

	.nav {
		display: grid;
		gap: 0.75rem;
	}

	.subtitle {
		font: 700 var(--f-h5);
		letter-spacing: var(--ls-h5);
	}

	.link {
		font: 500 var(--f-body2);
		letter-spacing: var(--ls-body2);
	}

	.address {
		font: 400 var(--f-body2);
		letter-spacing: var(--ls-body2);
		margin-block-start: 1rem;
		white-space: pre;
	}

	.copy + .copy {
		margin-block-start: 5.25rem;

		@media (--from-mobile) {
			margin-block-start: 2.25rem;
		}
	}

	.links {
		display: grid;
		font: 500 var(--f-body2);
		letter-spacing: var(--ls-body2);
		margin-block-start: 1rem;

		@media (--from-mobile) {
			font: 500 var(--f-body1);
			letter-spacing: var(--ls-body1);
		}

		span {
			text-decoration: underline;
		}

		a {
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
		}
	}

	.hr {
		background-color: var(--c-charcoal);
		grid-column: 1 / -1;
		margin-block: 2.5rem;
		opacity: 0.3;

		@media (--from-mobile) {
			margin-block: 2.875rem 3rem;
		}
	}

	.follow {
		align-items: center;
		gap: 1.5rem;
		grid-column: 1 / -1;
		padding-block-end: 2.875rem;

		@media (--from-mobile) {
			display: flex;
		}
	}

	.label {
		display: block;
		font: 600 var(--f-body1);
		letter-spacing: var(--ls-body1);
	}

	.field {
		align-items: center;
		display: flex;
		gap: 0.75rem;
		margin-block-start: 1.25rem;

		@media (--from-mobile) {
			margin-block-start: 0.75rem;
		}
	}

	.input {
		background-color: var(--c-white);
		border: none;
		border-radius: 0.5rem;
		color: var(--c-charcoal);
		font: 400 var(--f-body1);
		height: 3rem;
		letter-spacing: var(--ls-body1);
		padding: 0 1rem;
		width: 100%;

		@media (--from-mobile) {
			height: 3.5rem;
			width: 27.5rem;
		}
	}

	.submit {
		background-color: var(--c-rose);
		border-radius: var(--r-xs);
		display: grid;
		flex: 0 0 auto;
		place-items: center;
		size: 3rem;

		@media (--from-mobile) {
			size: 3.5rem;
		}
	}

	.icon {
		color: var(--c-white);
		size: 1rem 1.5rem;
	}

	.socials {
		display: flex;
		gap: 2.5rem;
		margin: 2.5rem 0 0 auto;

		@media (--until-mobile) {
			gap: 1.75rem;
			padding-inline: 0.5rem;
		}
	}

	.social {
		color: var(--c-charcoal);
		size: 2rem;

		@media (--from-mobile) {
			size: 2.625rem;
		}
	}

	.metas {
		align-items: center;
		display: flex;
		flex-direction: column;
		gap: 1rem;
		font: 400 var(--f-body2);
		grid-column: 1 / -1;
		letter-spacing: var(--ls-body2);
		padding-block-end: 0.75rem;

		@media (--from-mobile) {
			align-items: unset;
			flex-direction: row;
		}
	}

	.meta {
		flex: 1 0 auto;

		&:nth-child(n + 2) {
			flex: 0 0 auto;
		}
	}

	.end {
		inset: auto 0 0;
		position: fixed;
	}

	.pattern {
		background-image: var(--pattern);
		background-position: 75% 0%;
		size: 80vh 100%;
	}

	.logos {
		background-color: var(--c-white);
		display: flex;
		flex-wrap: wrap;
		gap: 0.5rem 2rem;
		justify-content: center;
		padding-block: 2.25rem;

		img {
			height: 3rem;
			flex: 0 0 auto;
			mix-blend-mode: multiply;
			object-fit: contain;

			@media (--from-mobile) {
				height: 4rem;
			}
		}
	}

	.error {
		color: var(--c-rose);
		font: 400 var(--f-body2);
		letter-spacing: var(--ls-body2);
		margin-block-start: 1rem;
	}
</style>
